import type { HighlightVariant } from '../../../../../components/FileViewer/pdf-viewer/highlight-layer/HighlightLayer.props';
import type { NavigateToDocumentReferencePayload } from '../../../../../context/EventBusContext/EventBusContext.type';
import type { CitationReferenceData } from '../../../../../services/reference-generator/types';
import type { ReviewItem } from '../../../../../store/useReviewStore';
import { statusToHighlightVariant } from '../../utilities/reviewAggregator';

/**
 * Everything the activation path needs to jump the drawer's document view to a
 * review item's source citation: the citation to make active (with its
 * status-derived highlight variant) and the NAVIGATE_TO_DOCUMENT_REFERENCE payload.
 */
export interface ReviewDocJump {
  /** Citation to set active in the citation context (drives the PDF highlight). */
  activeCitation: CitationReferenceData;
  /**
   * Highlight color variant, derived from the item's REVIEW STATUS (SCAL-8986),
   * not from the navigation source: pending -> AI-purple outline ('ai'),
   * accepted/dismissed -> plain yellow ('citation').
   */
  variant: HighlightVariant;
  /** Event-bus payload that opens the source document and scrolls to the citation. */
  navigatePayload: NavigateToDocumentReferencePayload;
}

/**
 * Build the doc-jump for a review card's PRIMARY (first) document-backed citation,
 * mirroring CitationValueViewer.onView (setActiveCitation + NAVIGATE_TO_DOCUMENT_REFERENCE).
 *
 * Returns null - a deliberate NO-OP - when the item has no document-backed citation
 * (AI-extraction-only or Excel-mapping-only items), so the caller does nothing for the
 * doc jump while the cell outline (slice 2) still fires. Token refs without OCR rects
 * still produce a jump; the PDF viewer scrolls without ever painting a highlight.
 */
export const buildReviewDocJump = (item: ReviewItem): ReviewDocJump | null => {
  const { primaryCitation } = item;
  if (!primaryCitation || primaryCitation.document_reference == null) {
    return null;
  }

  return {
    activeCitation: primaryCitation,
    // The item's review status stands in for the citation's own: review actions
    // settle a field and its citations together, and unlike the citation payload's
    // is_accepted mirror it can tell DISMISSED (plain yellow) apart from pending.
    variant: statusToHighlightVariant(item.status),
    navigatePayload: {
      documentReferenceId: primaryCitation.document_reference,
      expandDrawer: true,
      activeCitationData: primaryCitation,
    },
  };
};

/**
 * Everything the Feature C path needs to reveal a review item's Excel mapping
 * source: the NAVIGATE_TO_DOCUMENT_REFERENCE payload that opens the xlsx viewer to
 * the mapped sheet and highlights the mapped cell(s). Unlike ReviewDocJump there is
 * NO activeCitationData - xlsx highlighting is driven purely by cellReferences.
 */
export interface ReviewExcelJump {
  /** Event-bus payload that opens the source workbook and highlights the mapped cells. */
  navigatePayload: NavigateToDocumentReferencePayload;
}

/**
 * Build the xlsx-reveal jump for a review card's PRIMARY (first) Excel mapping
 * reference. Returns null - a deliberate NO-OP - when the item has no mapping
 * reference (citation-only / AI-only items), so a body click still selects + scrolls
 * the grid cell without touching the xlsx viewer. No activeCitationData is set
 * because xlsx highlighting reads cellReferences, not the PDF citation context.
 */
export const buildReviewExcelJump = (item: ReviewItem): ReviewExcelJump | null => {
  const { primaryMappingReference } = item;
  if (!primaryMappingReference || primaryMappingReference.document_reference == null) {
    return null;
  }

  return {
    navigatePayload: {
      documentReferenceId: primaryMappingReference.document_reference,
      sheetName: primaryMappingReference.sheet_name,
      expandDrawer: true,
      cellReferences: primaryMappingReference.cell_references.map(c => ({ ref: c.ref })),
    },
  };
};
